import { type createContactFormSchema } from "@/components/features/contact/AddOrEditContactModal/AddContactSlideover";
import type { SendTemplatedEmailPayload } from "@/lib/data/requests/contacts/contact-templated-email";
import type { SendMatterEmailPayload } from "@/lib/data/requests/matters/matter-email";
import type {
  MATTER_CONTACT_CONNECTION_LABEL_OPTIONS,
  MATTER_TEMPLATE_CATEGORY_OPTIONS,
} from "@/lib/data/static/matterStatics";
import type { ReportsSignatureRequestReport } from "@/lib/types/Reports";
import type { MatterContactLabelPair } from "@/lib/utils/matters/matter-contact-connection-party-roles";
import type {
  APIResponse,
  Contact,
  Matter,
  MatterContactConnection,
  MatterExpense,
  MatterFile,
  MatterIncidentReport,
  MatterInsurance,
  MatterInsuranceData,
  MatterLien,
  MatterLitigationDate,
  MatterLostWage,
  MatterDocumentSubItem,
  MedicalTreatment,
  MedicalTreatmentBalanceConfirmation,
  MedicalTreatmentAppointment,
  MedicalTreatmentBill,
  MedicalTreatmentPlan,
  MedicalTreatmentRecord,
  MatterNote,
  MatterPropertyDamage,
  MatterSettlementCounter,
  MatterSettlementDemand,
  MatterSettlementOffer,
  MatterSubItemType,
  MatterTask,
  MatterTrustCheck,
  MatterVehicle,
} from "@/lib/types";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  cleanupOrphanedModalSideEffects,
  MODAL_EXIT_ANIMATION_MS,
} from "@/lib/utils/modalSideEffectCleanup";
import type { z } from "zod";

export type SlideoverType =
  | "vehicle"
  | "insurance"
  | "medical-treatment"
  | "treatment-appointment"
  | "treatment-record"
  | "associate-treatment-appointments"
  | "treatment-plan"
  | "treatment-bill"
  | "treatment-balance-confirmation"
  | "treatment-balance-confirmation-history"
  | "settlement-demand"
  | "settlement-counter"
  | "expense"
  | "lost-wage"
  | "lien"
  | "matter-litigation-date"
  | "matter-litigation-date-plan-connection"
  | "property-damage"
  | "incident-report"
  | "matter-task"
  | "matter-note"
  | "matter"
  | "file"
  | "share-folder"
  | "share-folder-file"
  | "share-folder-access"
  | "generate-document"
  | "merge-document"
  | "communication"
  | "phone-call-log"
  | "send-email"
  | "send-matter-email"
  | "add-related-matter"
  | "select-file-repo-photos"
  | "intake-response"
  | "signature-request"
  | "firm-signature-request"
  | "create-signature-request"
  | "matter-trust-check"
  | "matter-contact-connection"
  | "case-incident-description"
  | "case-injuries"
  | "matter-critical-info"
  | "matter-email-view";

export interface BaseSlideoverPayload {
  type: SlideoverType;
  slideoverId?: string;
  title: string;
  description?: string;
  titleIcon?: React.ReactNode;
  size?: "default" | "large";
  enabledSections?: ("notes" | "tasks" | "references" | "events")[];
  initialTab?: "details" | "notes" | "tasks" | "references" | "events";
  editing?: boolean;
  isDirty?: boolean;
}

export interface VehicleSlideoverPayload extends BaseSlideoverPayload {
  type: "vehicle";
  matterId: string;
  vehicleId?: string;
  setSelectedVehicleId?: (vehicleId: string) => void;
  enforceRequiredContact?: "Client" | "Defendant";
  /** When set (e.g. other-party vehicle card), driver/passenger must include this contact. */
  requiredVehiclePartyContactId?: string;
  initialData?: Partial<MatterVehicle>;
  defendantContactIdContext?: string;
  vehiclePartyRole?: "Client" | "Defendant" | "OtherParty";
  sourceMatterContactConnectionId?: string;
}

export interface InsuranceSlideoverPayload extends BaseSlideoverPayload {
  type: "insurance";
  matterId: string;
  insuranceId?: string;
  afterCreateFn?: (newInsurance: APIResponse<MatterInsuranceData>) => void;
  enforceRequiredContact?: "Client" | "Defendant";
  /** When set (e.g. other-party card), this contact must be Insured or Policy Holder. */
  requiredInsurancePartyContactId?: string;
  initialData?: Partial<MatterInsurance>;
  defendantContactIdContext?: string;
  insurancePartyRole?: "Client" | "Defendant" | "OtherParty";
  sourceMatterContactConnectionId?: string;
}

export interface MedicalTreatmentSlideoverPayload extends BaseSlideoverPayload {
  type: "medical-treatment";
  matterId: string;
  treatmentId?: string;
  initialData?: Partial<MedicalTreatment>;
  initialProvider?: Contact;
}

export interface TreatmentAppointmentSlideoverPayload
  extends BaseSlideoverPayload {
  type: "treatment-appointment";
  matterId: string;
  appointmentId?: string;
  treatmentId?: string;
  initialData?: Partial<MedicalTreatmentAppointment>;
}

export interface TreatmentRecordSlideoverPayload extends BaseSlideoverPayload {
  type: "treatment-record";
  matterId: string;
  recordId?: string;
  treatmentId?: string;
  afterCreateFn?: (newRecord: APIResponse<MedicalTreatmentRecord>) => void;
  creatingFromAppointment?: boolean;
  initialData?: Partial<MedicalTreatmentRecord>;
}

export interface AssociateTreatmentAppointmentsSlideoverPayload
  extends BaseSlideoverPayload {
  type: "associate-treatment-appointments";
  matterId: string;
  treatmentId: string;
  appointmentIds: string[];
  onSuccess?: () => void;
}

export interface TreatmentPlanSlideoverPayload extends BaseSlideoverPayload {
  type: "treatment-plan";
  matterId: string;
  appointmentId?: string;
  planId?: string;
  treatmentId?: string;
  initialData?: Partial<MedicalTreatmentPlan>;
}

export interface TreatmentBillSlideoverPayload extends BaseSlideoverPayload {
  type: "treatment-bill";
  matterId: string;
  billId?: string;
  treatmentId?: string;
  afterCreateFn?: (newBill: APIResponse<MedicalTreatmentBill>) => void;
  creatingFromAppointment?: boolean;
  initialData?: Partial<MedicalTreatmentBill>;
}

export interface TreatmentBalanceConfirmationSlideoverPayload
  extends BaseSlideoverPayload {
  type: "treatment-balance-confirmation";
  matterId: string;
  treatmentId?: string;
  initialData?: Partial<MedicalTreatmentBalanceConfirmation>;
}

export interface TreatmentBalanceConfirmationHistorySlideoverPayload
  extends BaseSlideoverPayload {
  type: "treatment-balance-confirmation-history";
  matterId: string;
  treatmentId?: string;
}

export interface SettlementDemandSlideoverPayload extends BaseSlideoverPayload {
  type: "settlement-demand";
  matterId: string;
  settlementId?: string;
  negotiationType: "Demand" | "Offer";
  selectedInsurance?: MatterInsurance;
  isMedpay?: boolean;
  coverageType?: string;
  initialData?:
    | Partial<MatterSettlementDemand>
    | Partial<MatterSettlementOffer>;
}

export interface SettlementCounterSlideoverPayload
  extends BaseSlideoverPayload {
  type: "settlement-counter";
  matterId: string;
  settlementCounterId?: string;
  selectedInsurance?: MatterInsurance;
  coverageType?: string;
  initialData?: Partial<MatterSettlementCounter>;
}

export interface ExpenseSlideoverPayload extends BaseSlideoverPayload {
  type: "expense";
  matterId: string;
  expenseId?: string;
  initialData?: Partial<MatterExpense>;
  splitMode?: boolean;
}

export interface LostWageSlideoverPayload extends BaseSlideoverPayload {
  type: "lost-wage";
  matterId: string;
  lostWageId?: string;
  initialData?: Partial<MatterLostWage>;
}

export interface MatterLitigationDateSlideoverPayload
  extends BaseSlideoverPayload {
  type: "matter-litigation-date";
  matterId: string;
  litigationDateId?: string;
  initialData?: Partial<MatterLitigationDate>;
}

export interface MatterLitigationDatePlanConnectionModalPayload
  extends BaseSlideoverPayload {
  type: "matter-litigation-date-plan-connection";
  matterId: string;
}

export interface LienSlideoverPayload extends BaseSlideoverPayload {
  type: "lien";
  matterId: string;
  lienId?: string;
  initialData?: Partial<MatterLien>;
}

export interface PropertyDamageSlideoverPayload extends BaseSlideoverPayload {
  type: "property-damage";
  matterId: string;
  propertyDamageId?: string;
  initialData?: Partial<MatterPropertyDamage>;
}

export interface IncidentReportModalPayload extends BaseSlideoverPayload {
  type: "incident-report";
  matterId: string;
  incidentReportId?: string;
  initialData?: Partial<MatterIncidentReport>;
}

export interface MatterTaskSlideoverPayload extends BaseSlideoverPayload {
  type: "matter-task";
  matterId?: string;
  massTortId?: string;
  taskId?: string;
  initialData?: Partial<MatterTask>;
  hideMatterField?: boolean;
  subItem?: {
    id: string;
    type: MatterSubItemType;
  };
}

export interface MatterNoteSlideoverPayload extends BaseSlideoverPayload {
  type: "matter-note";
  matterId?: string;
  massTortId?: string;
  noteId?: string;
  initialData?: Partial<MatterNote>;
  hasEditPermission?: boolean;
  hasDeletePermission?: boolean;
  subItem?: {
    id: string;
    type: MatterSubItemType;
  };
}

export interface MatterTrustCheckSlideoverPayload extends BaseSlideoverPayload {
  type: "matter-trust-check";
  matterId: string;
  trustCheckId?: string;
  trustCheckType?: MatterTrustCheck["type"];
  settlementOfferId?: string;
  settlementDemandId?: string;
  settlementCounterId?: string;
  expenseId?: string;
  lienId?: string;
  medicalTreatmentBillId?: string;
  initialData?: Partial<MatterTrustCheck>;
}

export interface MatterSlideoverPayload extends BaseSlideoverPayload {
  type: "matter";
  matter?: unknown;
}

export interface FileSlideoverPayload extends BaseSlideoverPayload {
  type: "file";
  matterId?: string;
  massTortId?: string;
  activeFileId?: string;
  currentFolderId: string;
  isFolder: boolean;
  handleDeleteItem?: () => Promise<void>;
  isCriticalImage?: boolean;
  autoOpenUpload?: boolean;
  initialFile?: File;
  afterCreateFile?: (file: MatterFile) => Promise<void> | void;
  showPreview?: boolean;
}

export interface ShareFolderSlideoverPayload extends BaseSlideoverPayload {
  type: "share-folder";
  matterId: string;
  initialSelectedFileIds?: string[];
  initialSelectedContactIds?: string[];
  shareFolderId?: string;
}

export interface ShareFolderAccessSlideoverPayload
  extends BaseSlideoverPayload {
  type: "share-folder-access";
  matterId: string;
  shareFolderId: string;
}

export interface ShareFolderFileSlideoverPayload extends BaseSlideoverPayload {
  type: "share-folder-file";
  matterId: string;
  matterShareFolderId?: string;
  matterShareFolderFileId: string;
  matterShareFolderFilePresignedUrlKey: string;
  matterFileName: string;
  shareFolderLabel?: string;
}

export interface GenerateDocumentSlideoverPayload extends BaseSlideoverPayload {
  type: "generate-document";
  matter: Matter;
  contacts: Contact[];
  afterGenerateFn?: (results: MatterFile[]) => void;
  matterFileIdLocation?: string;
  templateType?: (typeof MATTER_TEMPLATE_CATEGORY_OPTIONS)[number];
  restrictTemplatesToType?: boolean;
  hideSaveAndMerge?: boolean;
  subItem?: MatterDocumentSubItem;
}

export interface MergeDocumentSlideoverPayload extends BaseSlideoverPayload {
  type: "merge-document";
  matterId?: string;
  massTortId?: string;
  initialSelectedFileIds?: string[];
}

export interface CommunicationSlideoverPayload extends BaseSlideoverPayload {
  type: "communication";
  contactId: string;
  matterId?: string;
  setSelectedContactId?: (contactId: string) => void;
  defaultMessage?: string;
}

export interface PhoneCallLogModalPayload extends BaseSlideoverPayload {
  type: "phone-call-log";
  contactId: string;
}

export interface SendEmailSlideoverPayload extends BaseSlideoverPayload {
  type: "send-email";
  toEmails: string[];
  contactIds: string[];
  templateName: string;
  templateData: SendTemplatedEmailPayload["templateData"];
  defaultCcEmails?: string[];
  defaultBccEmails?: string[];
  onSuccess?: () => void;
}

export interface SendMatterEmailSlideoverPayload extends BaseSlideoverPayload {
  type: "send-matter-email";
  matterId: string;
  contactId?: string;
  toEmails?: string[];
  templateName?: SendMatterEmailPayload["templateName"];
  templateData?: Partial<SendMatterEmailPayload["templateData"]>;
  matterFiles?: string[];
  onSuccess?: () => void;
}

export interface MatterEmailViewSlideoverPayload extends BaseSlideoverPayload {
  type: "matter-email-view";
  matterId: string;
  matterEmailId: string;
}

export interface AddRelatedMatterModalPayload extends BaseSlideoverPayload {
  type: "add-related-matter";
  matterId: string;
}

export interface SignatureRequestSlideoverPayload extends BaseSlideoverPayload {
  type: "signature-request";
  matterId: string;
  signatureRequestId?: string;
  presentation?: "slideover" | "modal";
}

export interface CreateSignatureRequestSlideoverPayload
  extends BaseSlideoverPayload {
  type: "create-signature-request";
  matterId?: string;
  subItem?: MatterDocumentSubItem;
}

export interface FirmSignatureRequestSlideoverPayload
  extends BaseSlideoverPayload {
  type: "firm-signature-request";
  reportRow: ReportsSignatureRequestReport;
}

export interface SelectFileRepoPhotosSlideoverPayload
  extends BaseSlideoverPayload {
  type: "select-file-repo-photos";
  matterId: string;
  onSelect: (files: unknown[]) => void;
}

export interface IntakeResponseModalPayload extends BaseSlideoverPayload {
  type: "intake-response";
  matterId: string;
}

export interface CaseIncidentDescriptionModalPayload
  extends BaseSlideoverPayload {
  type: "case-incident-description";
  matterId: string;
}

export interface CaseInjuriesModalPayload extends BaseSlideoverPayload {
  type: "case-injuries";
  matterId: string;
}

export interface MatterCriticalInfoSlideoverPayload
  extends BaseSlideoverPayload {
  type: "matter-critical-info";
  matterId: string;
}

export interface MatterContactConnectionSlideoverPayload
  extends BaseSlideoverPayload {
  type: "matter-contact-connection";
  matterId: string;
  /** Skip `retrieveContact` in the preview column when already available (e.g. matter page). */
  previewContact?: Contact;
  matterContactConnectionId?: string;
  matterContactConnectionContactId?: string;
  matterContactConnection?: MatterContactConnection;
  disableDelete?: boolean;
  disableTime?: boolean;
  labelOptions?: (typeof MATTER_CONTACT_CONNECTION_LABEL_OPTIONS)[number][];
  slideoverTitle?: string;
  autoOpenContactSelection?: boolean;
  selectContactOnly?: boolean;
  defaultCreateContactValues?: Partial<z.infer<typeof createContactFormSchema>>;
  defaultContactSearchObject?: { [key: string]: string[] };
  excludeContactIds?: string[];
  reservedContactLabelPairs?: MatterContactLabelPair[];
  editPresentation?: "slideover" | "modal";
  createPresentation?: "slideover" | "modal";
}

export type SlideoverPayload =
  | CaseIncidentDescriptionModalPayload
  | CaseInjuriesModalPayload
  | MatterCriticalInfoSlideoverPayload
  | CommunicationSlideoverPayload
  | PhoneCallLogModalPayload
  | SendEmailSlideoverPayload
  | SendMatterEmailSlideoverPayload
  | MatterEmailViewSlideoverPayload
  | ExpenseSlideoverPayload
  | FileSlideoverPayload
  | ShareFolderSlideoverPayload
  | ShareFolderFileSlideoverPayload
  | ShareFolderAccessSlideoverPayload
  | GenerateDocumentSlideoverPayload
  | InsuranceSlideoverPayload
  | IntakeResponseModalPayload
  | LienSlideoverPayload
  | LostWageSlideoverPayload
  | MatterContactConnectionSlideoverPayload
  | MatterLitigationDateSlideoverPayload
  | MatterLitigationDatePlanConnectionModalPayload
  | MatterNoteSlideoverPayload
  | MatterSlideoverPayload
  | MatterTaskSlideoverPayload
  | MatterTrustCheckSlideoverPayload
  | MedicalTreatmentSlideoverPayload
  | MergeDocumentSlideoverPayload
  | IncidentReportModalPayload
  | PropertyDamageSlideoverPayload
  | AddRelatedMatterModalPayload
  | SelectFileRepoPhotosSlideoverPayload
  | SettlementDemandSlideoverPayload
  | SettlementCounterSlideoverPayload
  | CreateSignatureRequestSlideoverPayload
  | FirmSignatureRequestSlideoverPayload
  | SignatureRequestSlideoverPayload
  | TreatmentAppointmentSlideoverPayload
  | AssociateTreatmentAppointmentsSlideoverPayload
  | TreatmentBillSlideoverPayload
  | TreatmentBalanceConfirmationSlideoverPayload
  | TreatmentBalanceConfirmationHistorySlideoverPayload
  | TreatmentPlanSlideoverPayload
  | TreatmentRecordSlideoverPayload
  | VehicleSlideoverPayload;

export interface ActiveSlideover {
  id: string;
  payload: SlideoverPayload;
  isOpen: boolean;
}

type ModalContextType = {
  activeSlideovers: ActiveSlideover[];
  openSlideover: (payload: SlideoverPayload) => string;
  closeSlideover: (id: string) => void;
  closeAllSlideovers: () => void;
  getSlideover: (id: string) => ActiveSlideover | undefined;
  updateSlideoverPayload: (
    id: string,
    updates: Partial<
      Pick<
        BaseSlideoverPayload,
        | "title"
        | "description"
        | "titleIcon"
        | "size"
        | "enabledSections"
        | "initialTab"
        | "editing"
        | "isDirty"
      >
    > &
      Partial<Pick<VehicleSlideoverPayload, "vehicleId">> &
      Partial<Pick<InsuranceSlideoverPayload, "insuranceId">> &
      Partial<Pick<MedicalTreatmentSlideoverPayload, "treatmentId">> &
      Partial<Pick<TreatmentAppointmentSlideoverPayload, "appointmentId">> &
      Partial<Pick<TreatmentRecordSlideoverPayload, "recordId">> &
      Partial<Pick<TreatmentPlanSlideoverPayload, "planId">> &
      Partial<Pick<TreatmentBillSlideoverPayload, "billId">> &
      Partial<Pick<SettlementDemandSlideoverPayload, "settlementId">> &
      Partial<Pick<SettlementCounterSlideoverPayload, "settlementCounterId">> &
      Partial<Pick<ExpenseSlideoverPayload, "expenseId">> &
      Partial<Pick<LostWageSlideoverPayload, "lostWageId">> &
      Partial<Pick<MatterLitigationDateSlideoverPayload, "litigationDateId">> &
      Partial<Pick<LienSlideoverPayload, "lienId">> &
      Partial<Pick<PropertyDamageSlideoverPayload, "propertyDamageId">> &
      Partial<Pick<IncidentReportModalPayload, "incidentReportId">> &
      Partial<
        Pick<
          MatterTaskSlideoverPayload,
          "taskId" | "matterId" | "hideMatterField"
        >
      > &
      Partial<Pick<MatterNoteSlideoverPayload, "noteId">> &
      Partial<Pick<MatterTrustCheckSlideoverPayload, "trustCheckId">> &
      Partial<Pick<SignatureRequestSlideoverPayload, "signatureRequestId">> &
      Partial<
        Pick<
          ShareFolderSlideoverPayload,
          "initialSelectedFileIds" | "initialSelectedContactIds"
        >
      > &
      Partial<Pick<ShareFolderAccessSlideoverPayload, "shareFolderId">> &
      Partial<Pick<CaseIncidentDescriptionModalPayload, "matterId">> &
      Partial<Pick<CaseInjuriesModalPayload, "matterId">> &
      Partial<Pick<MatterCriticalInfoSlideoverPayload, "matterId">>,
  ) => void;
};

interface ModalProviderHmrData {
  modalContext?: React.Context<ModalContextType | null>;
}

const modalProviderHmrData = import.meta.hot?.data as
  | ModalProviderHmrData
  | undefined;

const ModalContext =
  modalProviderHmrData?.modalContext ??
  createContext<ModalContextType | null>(null);

if (import.meta.hot) {
  const hotData = import.meta.hot.data as ModalProviderHmrData | undefined;
  if (hotData) {
    hotData.modalContext = ModalContext;
  }
}

function isMatterDefendantDetailsPath(pathname: string): boolean {
  return pathname.includes("/matter-details/defendant");
}

const SLIDEOVER_TYPES_ALLOWED_ON_MATTER_DEFENDANT_TAB = new Set<SlideoverType>([
  "matter-contact-connection",
  "create-signature-request",
  "communication",
  "phone-call-log",
  "generate-document",
  "insurance",
  "matter-note",
  "matter-task",
  "vehicle",
]);

function shouldBlockSlideoverOnCurrentPath(
  payload: SlideoverPayload,
  pathname: string,
): boolean {
  if (!isMatterDefendantDetailsPath(pathname)) return false;
  return !SLIDEOVER_TYPES_ALLOWED_ON_MATTER_DEFENDANT_TAB.has(payload.type);
}

export const ModalProvider = ({ children }: { children: React.ReactNode }) => {
  const [activeSlideovers, setActiveSlideovers] = useState<ActiveSlideover[]>(
    [],
  );
  const closeTimeoutsRef = useRef<Map<string, number>>(new Map());
  const closeAllTimeoutRef = useRef<number | null>(null);

  const generateSlideoverId = useCallback((payload: SlideoverPayload) => {
    return payload.slideoverId ?? payload.type;
  }, []);

  const clearCloseTimeout = useCallback((id: string) => {
    const timeoutId = closeTimeoutsRef.current.get(id);
    if (timeoutId != null) {
      window.clearTimeout(timeoutId);
      closeTimeoutsRef.current.delete(id);
    }
  }, []);

  const scheduleSlideoverRemoval = useCallback(
    (id: string) => {
      clearCloseTimeout(id);

      const timeoutId = window.setTimeout(() => {
        closeTimeoutsRef.current.delete(id);
        setActiveSlideovers((prev) =>
          prev.filter((slideover) => slideover.id !== id),
        );
        window.setTimeout(cleanupOrphanedModalSideEffects, 0);
      }, MODAL_EXIT_ANIMATION_MS);

      closeTimeoutsRef.current.set(id, timeoutId);
    },
    [clearCloseTimeout],
  );

  const openSlideover = useCallback(
    (payload: SlideoverPayload): string => {
      if (
        typeof window !== "undefined" &&
        shouldBlockSlideoverOnCurrentPath(payload, window.location.pathname)
      ) {
        return "";
      }

      const id = generateSlideoverId(payload);
      clearCloseTimeout(id);

      if (closeAllTimeoutRef.current != null) {
        window.clearTimeout(closeAllTimeoutRef.current);
        closeAllTimeoutRef.current = null;
      }

      setActiveSlideovers((prev) => {
        const existing = prev.find((slideover) => slideover.id === id);
        if (existing?.isOpen) {
          return prev;
        }

        if (existing) {
          return prev.map((slideover) =>
            slideover.id === id
              ? { ...slideover, payload, isOpen: true }
              : slideover,
          );
        }

        return [...prev, { id, payload, isOpen: true }];
      });

      return id;
    },
    [clearCloseTimeout, generateSlideoverId],
  );

  const closeSlideover = useCallback(
    (id: string) => {
      setActiveSlideovers((prev) =>
        prev.map((slideover) =>
          slideover.id === id ? { ...slideover, isOpen: false } : slideover,
        ),
      );
      scheduleSlideoverRemoval(id);
    },
    [scheduleSlideoverRemoval],
  );

  const closeAllSlideovers = useCallback(() => {
    closeTimeoutsRef.current.forEach((timeoutId) => {
      window.clearTimeout(timeoutId);
    });
    closeTimeoutsRef.current.clear();

    if (closeAllTimeoutRef.current != null) {
      window.clearTimeout(closeAllTimeoutRef.current);
    }

    setActiveSlideovers((prev) =>
      prev.map((slideover) => ({ ...slideover, isOpen: false })),
    );

    closeAllTimeoutRef.current = window.setTimeout(() => {
      closeAllTimeoutRef.current = null;
      setActiveSlideovers([]);
      window.setTimeout(cleanupOrphanedModalSideEffects, 0);
    }, MODAL_EXIT_ANIMATION_MS);
  }, []);

  const getSlideover = useCallback(
    (id: string) => {
      return activeSlideovers.find((slideover) => slideover.id === id);
    },
    [activeSlideovers],
  );

  const updateSlideoverPayload = useCallback(
    (
      id: string,
      updates: Partial<
        Pick<
          BaseSlideoverPayload,
          | "title"
          | "description"
          | "titleIcon"
          | "size"
          | "enabledSections"
          | "initialTab"
          | "editing"
          | "isDirty"
        >
      > &
        Partial<Pick<VehicleSlideoverPayload, "vehicleId">> &
        Partial<Pick<InsuranceSlideoverPayload, "insuranceId">> &
        Partial<Pick<MedicalTreatmentSlideoverPayload, "treatmentId">> &
        Partial<Pick<TreatmentAppointmentSlideoverPayload, "appointmentId">> &
        Partial<Pick<TreatmentRecordSlideoverPayload, "recordId">> &
        Partial<Pick<TreatmentBillSlideoverPayload, "billId">> &
        Partial<Pick<SettlementDemandSlideoverPayload, "settlementId">> &
        Partial<
          Pick<SettlementCounterSlideoverPayload, "settlementCounterId">
        > &
        Partial<Pick<ExpenseSlideoverPayload, "expenseId">> &
        Partial<Pick<LostWageSlideoverPayload, "lostWageId">> &
        Partial<
          Pick<MatterLitigationDateSlideoverPayload, "litigationDateId">
        > &
        Partial<Pick<LienSlideoverPayload, "lienId">> &
        Partial<Pick<PropertyDamageSlideoverPayload, "propertyDamageId">> &
        Partial<Pick<IncidentReportModalPayload, "incidentReportId">> &
        Partial<
          Pick<
            MatterTaskSlideoverPayload,
            "taskId" | "matterId" | "hideMatterField"
          >
        > &
        Partial<Pick<MatterNoteSlideoverPayload, "noteId">> &
        Partial<Pick<MatterTrustCheckSlideoverPayload, "trustCheckId">> &
        Partial<Pick<SignatureRequestSlideoverPayload, "signatureRequestId">> &
        Partial<
          Pick<
            ShareFolderFileSlideoverPayload,
            | "matterShareFolderFileId"
            | "matterShareFolderFilePresignedUrlKey"
            | "matterFileName"
            | "shareFolderLabel"
            | "matterShareFolderId"
          >
        >,
    ) => {
      setActiveSlideovers((prev) =>
        prev.map((slideover) =>
          slideover.id === id
            ? { ...slideover, payload: { ...slideover.payload, ...updates } }
            : slideover,
        ),
      );
    },
    [],
  );

  useEffect(() => {
    const closeTimeouts = closeTimeoutsRef.current;

    return () => {
      closeTimeouts.forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
      closeTimeouts.clear();

      if (closeAllTimeoutRef.current != null) {
        window.clearTimeout(closeAllTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      closeAllSlideovers();
    };
    const handlePopState = () => {
      closeAllSlideovers();
    };
    const handleUrlChange = () => {
      closeAllSlideovers();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);
    window.addEventListener("pushstate", handleUrlChange);
    window.addEventListener("replacestate", handleUrlChange);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("pushstate", handleUrlChange);
      window.removeEventListener("replacestate", handleUrlChange);
    };
  }, [closeAllSlideovers]);

  return (
    <ModalContext.Provider
      value={{
        activeSlideovers,
        openSlideover,
        closeSlideover,
        closeAllSlideovers,
        getSlideover,
        updateSlideoverPayload,
      }}
    >
      {children}
    </ModalContext.Provider>
  );
};

export const useModals = () => {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error("useModals must be used within a ModalProvider");
  return ctx;
};

export const useModalBreadcrumbs = () => {
  const { activeSlideovers } = useModals();
  return {
    slideovers: activeSlideovers
      .filter((s) => s.isOpen)
      .map((s) => ({ id: s.id, title: s.payload.title })),
  };
};
